import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Building2, ArrowLeft, Save, Clock, History, AlertTriangle, CheckCircle,
  TrendingUp, DollarSign, Percent, Activity, ChevronRight, RotateCcw,
  FileText, Download, Award, LineChart
} from 'lucide-react';
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import CountrySelector from '@/components/CountrySelector';
import FinancingSelector from '@/components/FinancingSelector';
import StressTestModal from '@/components/StressTestModal';
import { FREE_REPORT_GATE_MESSAGE, normalizeTier } from '@/utils/planMessaging';

const flagEmoji = { CA: '🇨🇦', US: '🇺🇸' };

export default function DealDetail() {
  const { dealId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [deal, setDeal] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [showStressTest, setShowStressTest] = useState(false);
  const [editedInput, setEditedInput] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [usageLimits, setUsageLimits] = useState(null);

  useEffect(() => {
    fetchDeal();
    fetchVersions();
  }, [dealId]);

  useEffect(() => {
    if (token) {
      fetchUsageLimits();
    }
  }, [token]);

  const fetchDeal = async () => {
    try {
      const response = await axios.get(`${API}/deals/${dealId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeal(response.data);
      setEditedInput(response.data.deal_input);
    } catch (error) {
      console.error('Failed to fetch deal:', error);
      toast.error('Deal not found');
      navigate('/deals');
    } finally {
      setLoading(false);
    }
  };

  const fetchVersions = async () => {
    try {
      const response = await axios.get(`${API}/deals/${dealId}/versions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVersions(response.data.versions || []);
    } catch (error) {
      console.error('Failed to fetch versions:', error);
    }
  };

  const fetchUsageLimits = async () => {
    try {
      const response = await axios.get(`${API}/auth/usage-limits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsageLimits(response.data);
    } catch (error) {
      console.error('Failed to fetch usage limits:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedInput(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/deals/${dealId}`, {
        deal_input: editedInput,
        change_summary: 'Updated deal inputs'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDeal(prev => ({
        ...prev,
        ...response.data,
        current_version: response.data.current_version
      }));
      setHasChanges(false);
      fetchVersions();
      toast.success('Deal saved - new version created');
    } catch (error) {
      toast.error('Failed to save deal');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (versionNumber) => {
    try {
      await axios.post(`${API}/deals/${dealId}/restore/${versionNumber}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Restored to version ${versionNumber}`);
      fetchDeal();
      fetchVersions();
      setShowVersions(false);
    } catch (error) {
      toast.error('Failed to restore version');
    }
  };

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    try {
      const response = await axios.post(`${API}/reports`, {
        deal_id: dealId,
        report_name: `${deal.property_name} - Analysis Report`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Download the report
      const downloadResponse = await axios.get(`${API}/reports/${response.data.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${deal.property_name.replace(/\s+/g, '_')}_Report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report generated and downloaded!');
      fetchUsageLimits();
    } catch (error) {
      const payload = error?.response?.data;
      const detail = payload?.detail;
      if (detail && typeof detail === 'object') {
        toast.error(detail.message || detail.error || FREE_REPORT_GATE_MESSAGE);
      } else if (payload && typeof payload === 'object') {
        toast.error(payload.message || payload.error || FREE_REPORT_GATE_MESSAGE);
      } else {
        toast.error(detail || FREE_REPORT_GATE_MESSAGE);
      }
    } finally {
      setGeneratingReport(false);
    }
  };

  if (loading || !deal) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  const { results, financing_validation, projections, deal_score } = deal;
  const currentTier = normalizeTier(user?.tier);
  const reportsUsedThisMonth = usageLimits?.usage?.reports_this_month ?? 0;
  const reportMonthlyLimit = usageLimits?.limits?.max_reports_per_month;
  const isStarter = currentTier === 'starter';
  const isFree = currentTier === 'free';
  const isProLike = currentTier === 'pro' || currentTier === 'developer';
  const starterLimitReached = isStarter && Number.isFinite(reportMonthlyLimit) && reportsUsedThisMonth >= reportMonthlyLimit;
  const disableGenerateReport = generatingReport || isFree || starterLimitReached;

  const reportPlanText = isFree
    ? "PDF reports require Starter or Pro."
    : isStarter
      ? "5 PDF reports/month"
      : "Unlimited PDF reports";
  
  const getGradeColor = (grade) => {
    if (!grade) return 'text-slate-400';
    if (grade.startsWith('A')) return 'text-emerald-500';
    if (grade.startsWith('B')) return 'text-cyan-500';
    if (grade.startsWith('C')) return 'text-amber-500';
    return 'text-red-500';
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/deals')} className="text-slate-400">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <span className="text-2xl">{flagEmoji[deal.country_code]}</span>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-lg font-semibold text-white">{deal.property_name}</h1>
                    {deal_score && (
                      <span className={`px-2 py-0.5 text-sm font-bold rounded ${
                        deal_score.grade?.startsWith('A') ? 'bg-emerald-500/20 text-emerald-400' :
                        deal_score.grade?.startsWith('B') ? 'bg-cyan-500/20 text-cyan-400' :
                        deal_score.grade?.startsWith('C') ? 'bg-amber-500/20 text-amber-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {deal_score.grade} ({deal_score.score})
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Clock className="h-3 w-3" />
                    Version {deal.current_version}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <ChatToggleButton />
              <Button
                variant="outline"
                onClick={handleGenerateReport}
                disabled={disableGenerateReport}
                className="text-slate-300 border-slate-700"
              >
                <FileText className="h-4 w-4 mr-2" />
                {generatingReport ? 'Generating...' : 'Report'}
              </Button>
              <div className="hidden md:flex flex-col leading-tight">
                <span className="text-xs text-slate-300">{reportPlanText}</span>
                {isStarter && (
                  <span className="text-xs text-slate-500">
                    {reportsUsedThisMonth} of 5 reports used this month
                  </span>
                )}
                {isProLike && (
                  <span className="text-xs text-slate-500">Unlimited report generation</span>
                )}
              </div>
              {starterLimitReached && (
                <Button
                  size="sm"
                  onClick={() => navigate('/upgrade')}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                >
                  Upgrade to Pro
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setShowVersions(true)}
                className="text-slate-300 border-slate-700"
              >
                <History className="h-4 w-4 mr-2" />
                History
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowStressTest(true)}
                className="text-slate-300 border-slate-700"
              >
                <Activity className="h-4 w-4 mr-2" />
                Stress Test
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/deals/${dealId}/performance`)}
                className="text-slate-300 border-slate-700"
              >
                <LineChart className="h-4 w-4 mr-2" />
                Performance
              </Button>
              {hasChanges && (
                <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Financing Validation Alert */}
        {financing_validation && !financing_validation.passes_all && (
          <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-400">Financing Alert</h3>
                <p className="text-sm text-slate-300 mt-1">
                  This deal may not qualify for {financing_validation.product_name}:
                </p>
                <ul className="mt-2 space-y-1">
                  {financing_validation.errors?.map((err, i) => (
                    <li key={i} className="text-sm text-red-400 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                      {err}
                    </li>
                  ))}
                  {financing_validation.warnings?.map((warn, i) => (
                    <li key={i} className="text-sm text-amber-400 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                      {warn}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Deal Score Card */}
        {deal_score && (
          <div className="mb-6 p-4 rounded-lg bg-slate-800/50 border border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`text-4xl font-bold ${getGradeColor(deal_score.grade)}`}>
                  {deal_score.grade}
                </div>
                <div>
                  <div className="text-lg font-semibold text-white">
                    {deal_score.grade_description}
                  </div>
                  <div className="text-sm text-slate-400">
                    Score: {deal_score.score}/100
                  </div>
                </div>
              </div>
              <div className="hidden md:flex items-center gap-4">
                {Object.entries(deal_score.breakdown || {}).map(([key, data]) => (
                  <div key={key} className="text-center px-3">
                    <div className="text-xs text-slate-400 capitalize">{key.replace('_', ' ')}</div>
                    <div className={`text-sm font-semibold ${
                      data.score >= 75 ? 'text-emerald-500' :
                      data.score >= 50 ? 'text-amber-500' : 'text-red-500'
                    }`}>
                      {Math.round(data.score)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {deal_score.recommendations?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-700">
                <div className="text-xs text-slate-400 mb-1">Model observations:</div>
                <ul className="space-y-1">
                  {deal_score.recommendations.slice(0, 3).map((rec, i) => (
                    <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                      <span className="text-amber-500 mt-1">•</span>
                      {rec}
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-xs text-slate-500">
                  Scores and observations are estimates based on the supplied
                  assumptions, not financial or investment advice.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <MetricCard
            label="Cap Rate"
            value={`${results?.cap_rate?.toFixed(2)}%`}
            icon={<Percent className="h-4 w-4" />}
          />
          <MetricCard
            label="Cash-on-Cash"
            value={`${results?.cash_on_cash?.toFixed(2)}%`}
            icon={<TrendingUp className="h-4 w-4" />}
          />
          <MetricCard
            label="DSCR"
            value={`${results?.dscr?.toFixed(2)}x`}
            icon={financing_validation?.passes_dscr ? 
              <CheckCircle className="h-4 w-4 text-emerald-500" /> : 
              <AlertTriangle className="h-4 w-4 text-red-500" />
            }
            highlight={!financing_validation?.passes_dscr}
          />
          <MetricCard
            label="Annual Cash Flow"
            value={`$${results?.annual_cash_flow?.toLocaleString()}`}
            icon={<DollarSign className="h-4 w-4" />}
            positive={results?.annual_cash_flow >= 0}
          />
        </div>

        <Tabs defaultValue="inputs" className="space-y-6">
          <TabsList className="bg-slate-800">
            <TabsTrigger value="inputs">Inputs</TabsTrigger>
            <TabsTrigger value="results">Results</TabsTrigger>
            <TabsTrigger value="projections">Projections</TabsTrigger>
          </TabsList>

          <TabsContent value="inputs" className="space-y-6">
            {/* Purchase & Financing */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Purchase & Financing</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <InputField label="Purchase Price" value={editedInput?.purchase_price} onChange={(v) => handleInputChange('purchase_price', v)} prefix="$" />
                <InputField label="Down Payment %" value={editedInput?.down_payment_percent} onChange={(v) => handleInputChange('down_payment_percent', v)} suffix="%" />
                <InputField label="Interest Rate" value={editedInput?.interest_rate} onChange={(v) => handleInputChange('interest_rate', v)} suffix="%" step="0.1" />
                <InputField label="Amortization" value={editedInput?.amortization_years} onChange={(v) => handleInputChange('amortization_years', v)} suffix="years" />
              </div>
              <div className="mt-4">
                <Label className="text-slate-300 text-sm">Financing Product</Label>
                <FinancingSelector
                  countryCode={deal.country_code}
                  value={editedInput?.financing_product_id}
                  onChange={(v) => handleInputChange('financing_product_id', v)}
                />
              </div>
            </div>

            {/* Income */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Income</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <InputField label="Monthly Rent" value={editedInput?.monthly_rent} onChange={(v) => handleInputChange('monthly_rent', v)} prefix="$" />
                <InputField label="Other Income" value={editedInput?.other_income} onChange={(v) => handleInputChange('other_income', v)} prefix="$" />
                <InputField label="Vacancy Rate" value={editedInput?.vacancy_rate} onChange={(v) => handleInputChange('vacancy_rate', v)} suffix="%" />
                <InputField label="Rent Growth" value={editedInput?.rent_growth_rate} onChange={(v) => handleInputChange('rent_growth_rate', v)} suffix="%" />
              </div>
            </div>

            {/* Expenses */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Expenses</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <InputField label="Property Taxes (Annual)" value={editedInput?.property_taxes_annual} onChange={(v) => handleInputChange('property_taxes_annual', v)} prefix="$" />
                <InputField label="Insurance (Annual)" value={editedInput?.insurance_annual} onChange={(v) => handleInputChange('insurance_annual', v)} prefix="$" />
                <InputField label="Maintenance %" value={editedInput?.maintenance_percent} onChange={(v) => handleInputChange('maintenance_percent', v)} suffix="%" />
                <InputField label="CapEx %" value={editedInput?.capex_percent} onChange={(v) => handleInputChange('capex_percent', v)} suffix="%" />
                <InputField label="Management %" value={editedInput?.management_percent} onChange={(v) => handleInputChange('management_percent', v)} suffix="%" />
                <InputField label="Utilities (Monthly)" value={editedInput?.utilities_monthly} onChange={(v) => handleInputChange('utilities_monthly', v)} prefix="$" />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="results">
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Analysis Results</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <ResultRow label="Purchase Price" value={`$${results?.purchase_price?.toLocaleString()}`} />
                <ResultRow label="Down Payment" value={`$${results?.down_payment?.toLocaleString()}`} />
                <ResultRow label="Loan Amount" value={`$${results?.loan_amount?.toLocaleString()}`} />
                <ResultRow label="LTV" value={`${results?.ltv}%`} highlight={!financing_validation?.passes_ltv} />
                <ResultRow label="Monthly Mortgage" value={`$${results?.monthly_mortgage?.toLocaleString()}`} />
                <ResultRow label="Annual Debt Service" value={`$${results?.annual_debt_service?.toLocaleString()}`} />
                <ResultRow label="Gross Income" value={`$${results?.gross_annual_income?.toLocaleString()}`} />
                <ResultRow label="Vacancy Loss" value={`$${results?.vacancy_loss?.toLocaleString()}`} />
                <ResultRow label="Operating Expenses" value={`$${results?.total_operating_expenses?.toLocaleString()}`} />
                <ResultRow label="NOI" value={`$${results?.noi?.toLocaleString()}`} highlight />
                <ResultRow label="Annual Cash Flow" value={`$${results?.annual_cash_flow?.toLocaleString()}`} positive={results?.annual_cash_flow >= 0} />
                <ResultRow label="Monthly Cash Flow" value={`$${results?.monthly_cash_flow?.toLocaleString()}`} positive={results?.monthly_cash_flow >= 0} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="projections">
            <div className="glass-card rounded-xl p-6 overflow-x-auto">
              <h3 className="text-lg font-semibold text-white mb-4">10-Year Projections</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left text-slate-400 py-2">Year</th>
                    <th className="text-right text-slate-400 py-2">Property Value</th>
                    <th className="text-right text-slate-400 py-2">NOI</th>
                    <th className="text-right text-slate-400 py-2">Cash Flow</th>
                    <th className="text-right text-slate-400 py-2">Equity</th>
                    <th className="text-right text-slate-400 py-2">CoC</th>
                  </tr>
                </thead>
                <tbody>
                  {projections?.map((p) => (
                    <tr key={p.year} className="border-b border-slate-800">
                      <td className="py-2 text-white">{p.year}</td>
                      <td className="py-2 text-right text-slate-300">${p.property_value?.toLocaleString()}</td>
                      <td className="py-2 text-right text-slate-300">${p.noi?.toLocaleString()}</td>
                      <td className={`py-2 text-right ${p.cash_flow >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        ${p.cash_flow?.toLocaleString()}
                      </td>
                      <td className="py-2 text-right text-slate-300">${p.equity?.toLocaleString()}</td>
                      <td className="py-2 text-right text-slate-300">{p.cash_on_cash?.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Version History Modal */}
      <Dialog open={showVersions} onOpenChange={setShowVersions}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <History className="h-5 w-5" />
              Version History
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {versions.map((v) => (
              <div
                key={v.id}
                className={`p-4 rounded-lg border ${
                  v.version_number === deal.current_version
                    ? 'bg-amber-500/10 border-amber-500'
                    : 'bg-slate-800/50 border-slate-700'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-white">v{v.version_number}</span>
                      {v.version_number === deal.current_version && (
                        <span className="text-xs px-2 py-0.5 rounded bg-amber-500 text-slate-900">Current</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-400 mt-1">{v.change_summary}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span>{new Date(v.created_at).toLocaleString()}</span>
                      <span>DSCR: {v.results?.dscr?.toFixed(2)}x</span>
                      <span>CoC: {v.results?.cash_on_cash?.toFixed(1)}%</span>
                    </div>
                  </div>
                  {v.version_number !== deal.current_version && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRestore(v.version_number)}
                      className="text-slate-300 border-slate-600"
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Restore
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Stress Test Modal */}
      <StressTestModal
        open={showStressTest}
        onOpenChange={setShowStressTest}
        dealId={dealId}
        currentDscr={results?.dscr}
        minDscr={financing_validation?.min_dscr}
      />
    </div>
  );
}

// Helper Components
function MetricCard({ label, value, icon, highlight, positive }) {
  return (
    <div className={`glass-card rounded-xl p-4 ${highlight ? 'border-red-500/50' : ''}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-slate-400">{label}</span>
        {icon}
      </div>
      <div className={`text-2xl font-bold font-mono ${
        positive === true ? 'text-emerald-500' :
        positive === false ? 'text-red-500' :
        highlight ? 'text-red-500' : 'text-white'
      }`}>
        {value}
      </div>
    </div>
  );
}

function InputField({ label, value, onChange, prefix, suffix, step = "1" }) {
  return (
    <div>
      <Label className="text-slate-400 text-xs">{label}</Label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">{prefix}</span>}
        <Input
          type="number"
          step={step}
          value={value || ''}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className={`bg-slate-950 border-slate-700 text-white ${prefix ? 'pl-7' : ''} ${suffix ? 'pr-12' : ''}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{suffix}</span>}
      </div>
    </div>
  );
}

function ResultRow({ label, value, highlight, positive }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-slate-800">
      <span className="text-slate-400">{label}</span>
      <span className={`font-mono ${
        positive === true ? 'text-emerald-500' :
        positive === false ? 'text-red-500' :
        highlight ? 'text-amber-500 font-semibold' : 'text-white'
      }`}>
        {value}
      </span>
    </div>
  );
}
